import React, { ChangeEvent, useEffect, useState } from "react";
import { ShopItem } from "../../components/shop-item";
import { useLocation, useNavigate } from "react-router-dom";
import { IProductAdmin, IProductImages } from "../../models/admin/products";
import { useSelector } from "react-redux";
import { IAppState } from "../../store/store";
import { environment } from "../../environments/environment";
import api from "../../api/api";
import styled from "styled-components";
import { Pagination, Skeleton, Stack } from "@mui/material";
import { Menu, MenuButton, MenuItem } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import blackAngleUp from "../../assets/icons/angle-up.png";
import { ProductCard } from "../../components/ProductCard";

const sortDropdown = [
  { name: "NONE", value: null },
  { name: "BEST SELLER", value: "best_seller" },
  { name: "BEST MATCH", value: "best_match" },
  { name: "PRICE LOW", value: "price_low" },
  { name: "PRICE HIGH", value: "price_high" },
  { name: "ALPHABETICALLY (A-Z)", value: "alphabetically_a" },
  { name: "ALPHABETICALLY (Z-A)", value: "alphabetically_z" },
];

export const Shop = () => { 
  const location = useLocation();
  const navigate = useNavigate();

  const [priceRange, setValue] = useState<number[]>([0, 250]);
  const [currentPage, setCurrentPage] = useState(1);
  const [categoriesSelected, setCategoriesSelected] = useState<string[]>([]);
  const [sort, setSort] = useState<string | null>(null);

  const [products, setProducts] = useState<IProductAdmin[] | null>(null);
  const [productImages, setProductImages] = useState<IProductImages[] | null>(null);
  const [totalPages, setTotalPages] = useState(1);
  const [ordersData, setOrdersData] = useState<{ [key: string]: number }>({});
  
    const [categoriesList, setCategoriesList] = useState<
      { name: string; short_name: string }[]
    >([]);
  
  
  const fetchCategoriesList = async () => {
      try {
        const response = await api.get(`${environment.baseUrl}/admin/categories`);
  
        if (response.data.code == 200) {
          setCategoriesList(response.data.data.all_categories);
        }
      } catch (error) {
        navigate("/shop");
      }
    };


  const fetchProducts = async () => {
    const params: any = { page: currentPage }

    if (categoriesSelected[0]) {
      params.categories = categoriesSelected[0].toLocaleLowerCase();
    }
    if (sort) {
      params.sort = sort;
    }
    try {
      const response = await api.get(`${environment.baseUrl}/product/fetch/list`, { params });

      const productImages = response.data.data.product_images;

      const extractedImages = Object.entries(productImages)
        .filter(
          ([product_uuid, images]) =>
            (images as { product_uuid: string; media_url: string }[]).length >
            0
        )
        .map(([product_uuid, images]) => {
          const firstImage = (
            images as { product_uuid: string; media_url: string }[]
          )[0];

          return {
            product_uuid: firstImage.product_uuid,
            media_url: firstImage.media_url,
          };
        });

      setProductImages(extractedImages);
      setProducts(response.data.data?.products?.rows ?? null);
      setOrdersData(response.data.data?.orders ?? {});
      setTotalPages(response.data.data?.products?.total_pages ?? 1);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handlePageChange = (event: ChangeEvent<unknown>, page: number) => {
    // Validate that page is a valid number
    if (isNaN(page) || page < 1) {
      page = 1;
    }
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
    const params = new URLSearchParams(location.search);
    params.set("page", page.toString());
    navigate(`?${params}`);
  };

  useEffect(() => {
    fetchCategoriesList();

    const params = new URLSearchParams(location.search);
    const page = params.get("page");
    const sort = params.get("sort");

    if (page) {
      const parsedPage = parseInt(page);
      // Only set the page if it's a valid number, otherwise default to 1
      if (!isNaN(parsedPage) && parsedPage > 0) {
        setCurrentPage(parsedPage);
      } else {
        setCurrentPage(1);
      }
    }
    if (sort) {
      setSort(sort);
    }

    fetchProducts();
  }, [currentPage, categoriesSelected, sort]);



  return (
    <>
     <Wrapper className="web_padding_y web_padding_x">
        <h2 className="web_heading text-left md:text-center">SHOP</h2>
        <div className="flex cont mt-5 md:mt-10">
        <Menu
          className="campaign_sort"
          menuButton={
            <MenuButton>
              <div className="flex items-center gap-2 group">
                <p className="font-bold flex gap-4">
                  <span className="sort">SORT BY</span>
                  <span className="font-normal group-hover:scale-[1.01] duration-700">
                    {sortDropdown.find((x) => x.value === sort)?.name}
                  </span>
                </p>
                <img
                  src={blackAngleUp}
                  className="w-4 group-hover:scale-[1.3] duration-700"
                />
              </div>
            </MenuButton>
          }
          direction="bottom"
          align="center"
          offsetY={24}
          arrow={true}
        >
          <div className="font-medium rounded grid  text-lg py-3 bg-white">
            {sortDropdown.map((item, index) => (
              <MenuItem
                className={`${sort === item.value ? "active_sort" : ""}`}
                onClick={() => {
                  setSort(item.value);
                  const params = new URLSearchParams(location.search);
                  if (item.value) {
                    params.set("sort", item.value);
                  } else {
                    params.delete("sort");
                  }
                  navigate(`?${params}`);
                }}
                key={index}
              >
                {item.name}
              </MenuItem>
            ))}
          </div>
        </Menu>

          <div className="flex justify-center mt-4">
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      variant="outlined"
                      shape="rounded"
                    />
                  </div>
          </div>
        {products && (
          <>
            <div className="grid gap-6 sm:[grid-template-columns:repeat(auto-fill,minmax(300px,_1fr))] lg:grid-cols-3">
              {products.map((product: IProductAdmin) => {
                const productImageUrl = productImages?.find(
                  (image) => image.product_uuid === product.uuid)?.media_url;
                // console.log(productImageUrl)
                return (
                  <ProductCard
                    key={product.uuid}
                    product={product}
                    productImage={productImageUrl || ""}
                    // ordersCount={ordersData[product.uuid] || 0}
                  />
                )
              })}
            </div>
            <div className="flex justify-center mt-8">
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                variant="outlined"
                shape="rounded"
              />
            </div>
          </>
          )}

        </Wrapper>
    </>
  );
}


const Wrapper = styled.main`
  .flex.cont {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 2.5rem;
    flex-wrap: wrap;
    gap: 20px;
  }
  .filter {
    display: flex;
    gap: 1.4rem;
    flex-wrap: wrap;
  }
  .sort {
    position: relative;
  }
  .sort::after {
    content: "";
    position: absolute;
    top: 10%;
    right: -0.52rem;
    height: 90%;
    background-color: black;
    width: 3px;
  }
`;
